import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Check, MapPin } from "@phosphor-icons/react";
import Header from "@/components/site/Header";
import Services from "@/components/site/Services";
import CTABand from "@/components/site/CTABand";
import Contact from "@/components/site/Contact";
import Footer from "@/components/site/Footer";
import Reveal from "@/components/site/Reveal";
import SEOHead from "@/components/site/SEOHead";
import { getCity } from "@/lib/cities";
import { PHONE, PHONE_TEL, IMAGES } from "@/lib/siteData";

const TRUST = ["Quality Workmanship", "Reliable Service", "Local & Family-Owned"];

export default function CityPage() {
  const { slug } = useParams();
  const data = getCity(slug);
  if (!data) return <Navigate to="/" replace />;

  const canonical = `https://b3fixit.com/contractor/${data.slug}`;
  const title = `${data.headline} | B3 Contracting LLC`;
  const description = `${data.headline}. Remodeling, HVAC, painting, fence building, roofing & repairs in ${data.city}, ${data.region}. Family-owned. Call ${PHONE} for a free estimate.`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "GeneralContractor",
    name: "B3 Contracting LLC",
    url: canonical,
    telephone: "+1-210-954-0804",
    email: "richard.blake4hire@yahoo.com",
    image: "https://b3fixit.com/logo.png",
    areaServed: [`${data.city} ${data.region}`, ...data.nearby.map((n) => `${n} TX`)],
    address: { "@type": "PostalAddress", addressLocality: data.city, addressRegion: data.region, addressCountry: "US" },
    sameAs: ["https://www.facebook.com/share/17xmK5UU1S/"],
  };

  return (
    <div className="relative bg-ink text-white font-body antialiased overflow-x-hidden">
      <SEOHead title={title} description={description} canonical={canonical} jsonLd={jsonLd} />
      <div className="grain" aria-hidden="true" />
      <Header />

      <section id="top" className="relative min-h-[85vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt={`Contractor working in ${data.city}, ${data.region}`} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
        </div>
        <div className="relative mx-auto max-w-7xl w-full px-5 sm:px-8 pb-16 pt-32">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-xs sm:text-sm uppercase tracking-[0.35em] text-gold mb-6">
            {data.city} • {data.region}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading font-bold tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.98] max-w-4xl">
            {data.headline.split(" in ")[0]} in <span className="text-gold">{data.city}, {data.region}</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-white/75 leading-relaxed">
            {data.blurb}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-9 flex flex-col sm:flex-row gap-4">
            <a href="#contact" className="group inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-8 py-4 hover:bg-gold-hover transition-colors">
              Get a Free Estimate
              <ArrowRight size={18} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-8 py-4 hover:border-gold hover:text-gold transition-colors">
              <Phone weight="fill" size={18} /> Call {PHONE}
            </a>
          </motion.div>
          <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
            {TRUST.map((t) => (
              <li key={t} className="flex items-center gap-2 text-sm text-white/80"><Check size={16} weight="bold" className="text-gold" />{t}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-ink">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-heading font-bold tracking-tight text-white text-3xl sm:text-4xl leading-tight">
              Your local contractor in {data.city}
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="mt-6 text-lg text-white/70 leading-relaxed">{data.intro}</p>
          </Reveal>
        </div>
      </section>

      <Services />

      <section id="service-area" className="py-20 bg-ink border-t border-line">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-5">Also serving near {data.city}</p>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {data.nearby.map((n) => (
              <span key={n} className="inline-flex items-center gap-2 border border-line px-4 py-2.5 text-white/80">
                <MapPin size={16} weight="fill" className="text-gold" /> {n}, TX
              </span>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
      <Contact />
      <Footer />
    </div>
  );
}
